// src/cockpit4/components/APIPlatformPanel.tsx
// ============================================
// API 开放平台（设置中心 P1-功能4 深度扩展）
// RESTful API 文档 / API 密钥管理（作用域+撤销） / 第三方应用接入 /
// 版本管理 / 调用统计（热插拔，无需重启）
// 数据来源：integrationConfig（localStorage，subscribe 热更新）
// ============================================

import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  KeyRound, Copy, Plus, Check, ExternalLink, Shield, Cpu,
  Lock, Activity, Eye, EyeOff, Ban, GitBranch,
} from "lucide-react";
import { integrationConfig, maskKey } from "../../services/integrationConfig";
import type { ApiKeyRecord, ApiAppRecord } from "../../services/integrationConfig";

const API_ENDPOINTS = [
  { method: "GET", path: "/vault-api/health", desc: "服务健康检查", version: "v1", auth: false },
  { method: "GET", path: "/vault-api/list?dir=&ext=.md", desc: "列出目录文件", version: "v1", auth: true },
  { method: "GET", path: "/vault-api/list-all?dir=.&ext=.md", desc: "递归列出全部笔记", version: "v1", auth: true },
  { method: "GET", path: "/vault-api/read?path=", desc: "读取笔记内容", version: "v1", auth: true },
  { method: "POST", path: "/vault-api/write?path=", desc: "写入/更新笔记", version: "v1", auth: true },
  { method: "GET", path: "/vault-api/download?path=", desc: "下载文件（Excel/图片）", version: "v1", auth: true },
  { method: "POST", path: "/vault-api/upload", desc: "上传文件", version: "v1", auth: true },
  { method: "POST", path: "/vault-api/switch-vault", desc: "切换企业大脑（Vault）", version: "v2", auth: "admin" },
  { method: "GET", path: "/vault-api/config", desc: "读取 Vault 配置", version: "v2", auth: true },
  { method: "GET", path: "/quant-api/realtime?symbol=", desc: "量化实时行情", version: "v1", auth: true },
];

const SCOPE_LABEL: Record<string, { label: string; color: string }> = {
  read: { label: "只读", color: "#38BDF8" },
  write: { label: "读写", color: "#34D399" },
  admin: { label: "管理员", color: "#FBBF24" },
};

export default function APIPlatformPanel() {
  const [keys, setKeys] = useState<ApiKeyRecord[]>(integrationConfig.getApiKeys());
  const [apps, setApps] = useState<ApiAppRecord[]>(integrationConfig.getApiApps());
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyScope, setNewKeyScope] = useState<ApiKeyRecord["scope"]>("read");
  const [newAppName, setNewAppName] = useState("");
  const [newAppDesc, setNewAppDesc] = useState("");
  const [shownKey, setShownKey] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [tab, setTab] = useState<"overview" | "keys" | "docs" | "apps">("overview");

  useEffect(() => {
    const unsub = integrationConfig.subscribe(() => {
      setKeys(integrationConfig.getApiKeys());
      setApps(integrationConfig.getApiApps());
    });
    return unsub;
  }, []);

  const stats = useMemo(() => integrationConfig.getStats(), [keys, apps]);

  const createKey = () => {
    if (!newKeyName.trim()) return;
    const record = integrationConfig.createApiKey(newKeyName.trim(), newKeyScope, "本地控制台");
    setFlash(`密钥已创建（仅本次显示）：${record.key}`);
    setNewKeyName("");
    setShownKey(record.id);
    setTimeout(() => setFlash(null), 12000);
  };

  const copyText = (text: string, id: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const addApp = () => {
    if (!newAppName.trim()) return;
    integrationConfig.addApiApp(newAppName.trim(), newAppDesc.trim() || "第三方应用", ["read"]);
    setNewAppName("");
    setNewAppDesc("");
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <KeyRound size={14} className="text-sky-400" />
        <h3 className="text-[12px] font-medium text-white">API 开放平台</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P1-功能4 · RESTful API</span>
        <div className="ml-auto flex items-center gap-1 text-[9px] font-mono text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> 热插拔已启用
        </div>
      </div>

      {flash && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-[10px] font-mono text-amber-300 break-all">
          {flash}
        </motion.div>
      )}

      <div className="grid grid-cols-4 gap-2">
        {[
          { label: "有效密钥", value: stats.activeKeys, color: "#38BDF8", icon: KeyRound },
          { label: "已接入应用", value: apps.length, color: "#A78BFA", icon: Cpu },
          { label: "累计调用", value: stats.totalCalls, color: "#34D399", icon: Activity },
          { label: "活跃订阅", value: stats.activeWebhooks, color: "#FBBF24", icon: Lock },
        ].map((m) => (
          <div key={m.label} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
            <div className="flex items-center gap-1 text-[9px] font-mono text-gray-500 uppercase">
              <m.icon size={10} style={{ color: m.color }} /> {m.label}
            </div>
            <div className="text-lg font-semibold mt-0.5" style={{ color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-1">
        {([["overview", "总览"], ["keys", "密钥管理"], ["apps", "应用接入"], ["docs", "API 文档"]] as const).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-2.5 py-1 rounded-md text-[10px] transition-colors ${tab === id ? "bg-sky-500/20 text-sky-300 border border-sky-500/30" : "text-gray-400 border border-white/[0.06] hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Shield size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">平台能力</span>
          </div>
          <ul className="space-y-1 text-[10px] text-gray-400">
            <li>· RESTful API：vault-api（知识库读写）+ quant-api（量化行情）共 {API_ENDPOINTS.length} 个端点</li>
            <li>· 鉴权：Bearer Token（xk_ 前缀密钥），支持 read / write / admin 三级作用域</li>
            <li>· 限流：按作用域配置每分钟/每小时请求上限，超额自动拦截</li>
            <li>· 版本：v1（稳定）/ v2（热插拔新增端点），向后兼容</li>
            <li>· SDK：前端统一通过 integrationConfig 接入，配置变更即时生效（无需重启）</li>
          </ul>
        </div>
      )}

      {tab === "keys" && (
        <div className="space-y-2">
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-300 uppercase tracking-wider mb-2">创建 API 密钥</div>
            <div className="flex gap-2">
              <input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="密钥名称（如：小程序后端）"
                className="flex-1 px-2 py-1.5 rounded-md bg-black/30 border border-white/[0.08] text-[11px] text-white placeholder-gray-600 outline-none focus:border-sky-500/50" />
              <select value={newKeyScope} onChange={(e) => setNewKeyScope(e.target.value as any)}
                className="px-2 py-1.5 rounded-md bg-black/30 border border-white/[0.08] text-[11px] text-gray-300 outline-none">
                <option value="read">只读</option>
                <option value="write">读写</option>
                <option value="admin">管理员</option>
              </select>
              <button onClick={createKey}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-sky-500/20 border border-sky-500/30 text-[11px] text-sky-300 hover:bg-sky-500/30">
                <Plus size={11} /> 生成
              </button>
            </div>
          </div>

          {keys.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/[0.08] p-4 text-center text-[10px] text-gray-500">
              暂无 API 密钥，点击上方「生成」创建
            </div>
          ) : (
            keys.map((k) => (
              <div key={k.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${k.status === "active" ? "bg-emerald-400" : "bg-red-400"}`} />
                  <span className="text-[11px] text-white font-medium">{k.name}</span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-mono" style={{ background: `${SCOPE_LABEL[k.scope].color}22`, color: SCOPE_LABEL[k.scope].color }}>
                    {SCOPE_LABEL[k.scope].label}
                  </span>
                  <span className="text-[9px] font-mono text-gray-500">{k.app}</span>
                  <span className="ml-auto text-[9px] font-mono text-gray-500">调用 {k.callCount} 次</span>
                  <button onClick={() => setShownKey(shownKey === k.id ? null : k.id)} className="text-gray-500 hover:text-white">
                    {shownKey === k.id ? <EyeOff size={12} /> : <Eye size={12} />}
                  </button>
                  <button onClick={() => copyText(shownKey === k.id ? k.key : maskKey(k.key), k.id)} className="text-gray-500 hover:text-white">
                    {copied === k.id ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                  </button>
                  {k.status === "active" && (
                    <button onClick={() => integrationConfig.revokeApiKey(k.id)} className="text-gray-500 hover:text-red-400">
                      <Ban size={12} />
                    </button>
                  )}
                </div>
                <div className="mt-1 font-mono text-[10px] text-gray-500">
                  {shownKey === k.id ? k.key : maskKey(k.key)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {tab === "apps" && (
        <div className="space-y-2">
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
            <div className="text-[10px] font-mono text-gray-300 uppercase tracking-wider mb-2">接入第三方应用</div>
            <div className="flex gap-2">
              <input value={newAppName} onChange={(e) => setNewAppName(e.target.value)}
                placeholder="应用名称（如：酒店小程序）"
                className="flex-1 px-2 py-1.5 rounded-md bg-black/30 border border-white/[0.08] text-[11px] text-white placeholder-gray-600 outline-none focus:border-sky-500/50" />
              <input value={newAppDesc} onChange={(e) => setNewAppDesc(e.target.value)}
                placeholder="应用描述"
                className="flex-1 px-2 py-1.5 rounded-md bg-black/30 border border-white/[0.08] text-[11px] text-white placeholder-gray-600 outline-none focus:border-sky-500/50" />
              <button onClick={addApp}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-violet-500/20 border border-violet-500/30 text-[11px] text-violet-300 hover:bg-violet-500/30">
                <Plus size={11} /> 注册
              </button>
            </div>
          </div>
          {apps.map((a) => (
            <div key={a.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5 flex items-center gap-2">
              <Cpu size={12} className="text-violet-400" />
              <span className="text-[11px] text-white">{a.name}</span>
              <span className="text-[9px] text-gray-500 truncate">{a.description}</span>
              <span className="ml-auto px-1.5 py-0.5 rounded text-[9px] font-mono bg-emerald-500/15 text-emerald-300">{a.scopes.join("/")}</span>
              <span className="text-[9px] font-mono text-gray-500">{new Date(a.createdAt).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "docs" && (
        <div className="space-y-1.5">
          {API_ENDPOINTS.map((ep, i) => (
            <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2">
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-bold ${ep.method === "GET" ? "bg-sky-500/15 text-sky-300" : "bg-emerald-500/15 text-emerald-300"}`}>
                {ep.method}
              </span>
              <code className="font-mono text-[10px] text-gray-300">{ep.path}</code>
              <span className="text-[9px] text-gray-500 truncate">{ep.desc}</span>
              <span className="ml-auto text-[9px] font-mono text-gray-600">{ep.version}</span>
              <span className={`text-[9px] font-mono ${ep.auth === false ? "text-gray-600" : ep.auth === "admin" ? "text-amber-400" : "text-sky-400"}`}>
                {ep.auth === false ? "公开" : ep.auth === "admin" ? "admin" : "Bearer"}
              </span>
            </div>
          ))}
          <div className="rounded-lg border border-dashed border-white/[0.08] p-3 mt-2">
            <div className="flex items-center gap-1.5 mb-1.5">
              <GitBranch size={11} className="text-purple-400" />
              <span className="text-[10px] font-mono text-gray-300 uppercase">版本管理</span>
            </div>
            <div className="space-y-1 text-[10px] text-gray-400">
              <div className="flex items-center gap-2">
                <span className="px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 font-mono">v2 (latest)</span>
                <span>热插拔端点：switch-vault / config · 新增 webhook-test（Webhook 控制台）</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-1.5 py-0.5 rounded bg-gray-500/15 text-gray-400 font-mono">v1 (stable)</span>
                <span>知识库读写 / 列表 / 下载 / 上传 · 量化行情</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-500">
                <ExternalLink size={10} /> SDK 接入示例：
                <code className="font-mono">curl -H "Authorization: Bearer xk_..." {`${"API_BASE"}/vault-api/list?dir=.`}</code>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


